import PropTypes from "prop-types";
import { useState } from "react";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { Box, Dialog, DialogActions, DialogContent, DialogTitle, LinearProgress, MenuItem, TextField, Typography } from "@mui/material";
import ActionButton from "src/components/shared/ActionButton";
import { casesApi } from "./api";
import { ownerCounts } from "./ListFilters";

const AT_ONCE = 4;

// Gives many cases one owner (or none), four at a time, then one summary.
// Customers whose application is submitted are emailed who is reviewing it,
// as with one case; the dialog says so above the list, where the open menu
// never hides it (rania-15). Each person shows with the open cases they
// have and how many are late (owners, from the list; rania-08), so the work
// can be balanced. initialOwner: "Assign to me" opens it with me chosen.
export default function BulkAssign({ cases, agents, owners = [], initialOwner = "", onClose, onDone }) {
  const dispatch = useDispatch();
  const [owner, setOwner] = useState(initialOwner);
  const [error, setError] = useState("");
  const [progress, setProgress] = useState(null); // { done, failed }
  const running = progress && progress.done < cases.length;
  const name = owner === "none" ? null : agents.find((a) => String(a.id) === owner)?.name;

  const run = async () => {
    if (!owner) {
      setError("Choose who owns them.");
      return;
    }
    let done = 0;
    let failed = 0;
    setProgress({ done, failed });
    const queue = [...cases];
    const adminId = owner === "none" ? null : Number(owner);
    const worker = async () => {
      while (queue.length) {
        const item = queue.shift();
        try {
          // eslint-disable-next-line no-await-in-loop
          await casesApi.assign(item.id, adminId, dispatch, true);
        } catch {
          failed += 1;
        }
        done += 1;
        setProgress({ done, failed });
      }
    };
    await Promise.all(Array.from({ length: Math.min(AT_ONCE, cases.length) }, worker));
    const ok = cases.length - failed;
    const who = name ? `to ${name}` : "to nobody";
    if (failed) toast.warn(`Assigned ${ok} of ${cases.length} ${who}. ${failed} failed; try them again.`);
    else toast.success(`Assigned ${ok === 1 ? "1 case" : `${ok} cases`} ${who}.`);
    onDone();
  };

  return (
    <Dialog open onClose={running ? undefined : onClose} maxWidth="xs" fullWidth aria-labelledby="bulk-assign-title">
      <DialogTitle id="bulk-assign-title">Assign {cases.length === 1 ? "1 case" : `${cases.length} cases`}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }} id="bulk-assign-email">
          {owner === "none"
            ? "Removing the owner emails no one."
            : "Customers whose application is submitted are emailed who is reviewing it. Drafts and closed cases are not."}
        </Typography>
        <TextField
          select
          fullWidth
          label="Owner"
          value={owner}
          disabled={!!progress}
          onChange={(e) => {
            setOwner(e.target.value);
            setError("");
          }}
          error={!!error}
          helperText={error || undefined}
          inputProps={{ "aria-describedby": "bulk-assign-email" }}
          sx={{ mt: 1 }}
        >
          {agents.length === 0 && (
            <MenuItem value="" disabled>
              Loading the team…
            </MenuItem>
          )}
          {agents.map((a) => {
            const load = owners.find((o) => o.id === a.id);
            return (
              <MenuItem key={a.id} value={String(a.id)}>
                {a.name}
                {a.is_me ? " (me)" : ""}
                <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                  {load ? ownerCounts(load) : "0 open"}
                </Typography>
              </MenuItem>
            );
          })}
          <MenuItem value="none">Nobody (remove the owner)</MenuItem>
        </TextField>
        {progress && (
          <Box mt={2} role="status" aria-live="polite">
            <LinearProgress variant="determinate" value={(progress.done / cases.length) * 100} sx={{ height: 6, borderRadius: 3 }} />
            <Typography variant="body2" color="text.secondary" mt={0.75}>
              {running ? `Assigning ${progress.done} of ${cases.length}…` : `Done: ${cases.length - progress.failed} assigned`}
              {progress.failed ? `, ${progress.failed} failed` : ""}.
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        <ActionButton variant="text" size="medium" onClick={onClose} disabled={!!running}>
          Cancel
        </ActionButton>
        <ActionButton variant="contained" size="medium" busyLabel="Assigning…" disabled={!!progress} onClick={run}>
          {name ? `Assign to ${name}` : owner === "none" ? "Remove the owner" : "Assign"}
        </ActionButton>
      </DialogActions>
    </Dialog>
  );
}

BulkAssign.propTypes = {
  cases: PropTypes.array.isRequired,
  agents: PropTypes.array.isRequired,
  owners: PropTypes.array,
  initialOwner: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onDone: PropTypes.func.isRequired,
};
